/* global tinyMCE */
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import Toggle from "@shared/v2/Toggle";
import Typography from "@shared/v2/Typography";
import Button from "@shared/v2/Button";
import TextInput from "@shared/v2/TextInput";
import BrivityTextEditor from "@shared/BrivityTextEditor";
import AiAssistant from "@shared/AiAssistant";
import { debounce } from "lodash";
import { remark } from "remark";
import remarkRehype from "remark-rehype";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import dayjs from "@shared/dayjs";
import formatters from "@shared/formatters";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import ImageDropTarget from "../shared/ImageDropTarget";
import CollapsibleWebsiteCard from "../shared/CollapsibleWebsiteCard";
import { usePage, useSiteUuid, usePageUuid } from "../../reducers/websiteReducer/areaPageSelectors";

const EDITOR_ID = "market-stats-description";

// Keeps digits and a single decimal point (max 2 decimal places), e.g. "1,234.567" => "1234.56"
const toRawDecimal = (value) => {
  const [whole, ...rest] = String(value ?? "")
    .replace(/[^0-9.]/g, "")
    .split(".");
  return rest.length ? `${whole}.${rest.join("").slice(0, 2)}` : whole;
};

// Adds commas to the whole part only, so partial input like "1234." stays editable
const formatDecimalWithCommas = (value) => {
  const raw = toRawDecimal(value);
  if (!raw) return "";
  const [whole, decimals] = raw.split(".");
  const formattedWhole = formatters.number(whole || 0, 0);
  return decimals === undefined ? formattedWhole : `${formattedWhole}.${decimals}`;
};

const MarketStatsPanel = ({ data = undefined, setMarketStatsData }) => {
  const page = usePage();
  const siteUuid = useSiteUuid();
  const pageUuid = usePageUuid();
  // stat1Value is stored as a raw decimal (cents kept, e.g. "250000.5"); "$" is display-only
  const stat1Display = formatDecimalWithCommas(data?.stat1Value);

  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const commitRef = useRef(null);
  useEffect(() => () => commitRef.current?.cancel(), []);

  const uploadFile = (file) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("siteUuid", siteUuid);
    formData.append("pageUuid", pageUuid);
    formData.append("componentUuid", data.uuid);

    caseTransformingAxios.post("/website/upload_image_to_aws", formData).then((res) => {
      setMarketStatsData({
        ...data,
        imageUrl: res?.data?.url,
      });
    });
  };

  return (
    <CollapsibleWebsiteCard
      title="Market Stats"
      disabled={!data?.isActive}
      action={
        <Toggle
          checked={data?.isActive}
          onChange={(e) => {
            setMarketStatsData({
              ...data,
              isActive: e.target.checked,
            });
          }}
        />
      }
    >
      <div className="tw-flex  tw-gap-[24px] ">
        <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[24px]">
          <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[24px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl tw-h-[228px]">
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              {!page?.isCustomArea && (
                <>
                  <Typography variant="subtitle-m-bold">Stats (Custom Area)</Typography>
                  <Typography className="!tw-text-gray-50">
                    Keep your site viewers updated on the current state of your market! Make sure stats always
                    stay as accurate as possible. You can update these at any time.
                  </Typography>
                </>
              )}
              {page?.isCustomArea && (
                <>
                  <Typography variant="subtitle-m-bold">Stats For {data?.eyebrow}</Typography>
                  <Typography className="!tw-text-gray-50">
                    Market stats are updated automatically from MLS data and are not editable. Stats shown are
                    for the last 30 days.
                  </Typography>
                </>
              )}
            </div>
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <div className="tw-flex tw-justify-between tw-items-center">
                <Typography className="!tw-text-gray-50">Average Listing Price</Typography>
                {/* {page?.isCustomArea && <TextLink>Add</TextLink>}
                {!page?.isCustomArea && <Typography>{format.currency(data?.stat1Value, 0)}</Typography>} */}
                <TextInput
                  // className="tw-h-[28px]"
                  label=""
                  placeholder="Average listing price"
                  value={stat1Display ? `$${stat1Display}` : ""}
                  type="text"
                  onChange={(e) => {
                    setMarketStatsData({
                      ...data,
                      stat1Value: toRawDecimal(e.target.value),
                    });
                  }}
                />
              </div>
              <div className="tw-flex tw-justify-between tw-items-center">
                <Typography className="!tw-text-gray-50">Active Listings</Typography>
                {/* {page?.isCustomArea && <TextLink>Add</TextLink>}
                {!page?.isCustomArea && <Typography>{data?.stat2Value}</Typography>} */}
                <TextInput
                  // className="tw-h-[28px]"
                  label=""
                  placeholder="Active listings"
                  value={formatDecimalWithCommas(data?.stat2Value)}
                  type="text"
                  onChange={(e) => {
                    setMarketStatsData({
                      ...data,
                      stat2Value: toRawDecimal(e.target.value),
                    });
                  }}
                />
              </div>
              <div className="tw-flex tw-justify-between tw-items-center">
                <Typography className="!tw-text-gray-50">Average Days On Market</Typography>
                {/* {page?.isCustomArea && <TextLink>Add</TextLink>}
                {!page?.isCustomArea && <Typography>{data?.stat3Value}</Typography>} */}
                <TextInput
                  // className="tw-h-[28px]"
                  label=""
                  placeholder="Average days on market"
                  value={formatDecimalWithCommas(data?.stat3Value)}
                  type="text"
                  onChange={(e) => {
                    setMarketStatsData({
                      ...data,
                      stat3Value: toRawDecimal(e.target.value),
                    });
                  }}
                />
              </div>
            </div>
            {!page?.isCustomArea && (
              <Typography className="!tw-text-gray-50 !tw-italic">
                Last updated on {data?.dateUpdated ? dayjs(data?.dateUpdated).format("MM/DD/YYYY") : "—"}
              </Typography>
            )}
          </div>

          <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[16px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl tw-max-h-[278px]">
            <Typography variant="subtitle-m-bold">Image</Typography>
            <Typography className="!tw-text-gray-50">Background Image</Typography>
            <div className="tw-flex tw-flex-col tw-max-w-[250px] tw-gap-[24px]">
              {data?.imageUrl && (
                <div className="tw-flex tw-flex-col tw-gap-[8px]">
                  <img
                    className="tw-w-full tw-h-[150px] tw-rounded-[8px] tw-object-cover"
                    src={data?.imageUrl}
                    alt="Featured"
                  />
                  <div className="tw-flex tw-justify-between tw-items-center tw-gap-[8px]">
                    <Button
                      className="tw-flex-1"
                      schema="warning"
                      size="small"
                      onClick={() => setMarketStatsData({ ...data, imageUrl: null })}
                    >
                      Delete
                    </Button>
                    <Button
                      className="tw-flex-1"
                      size="small"
                      onClick={() => {
                        const input = document.createElement("input");
                        input.type = "file";
                        input.accept = "image/*";
                        input.onchange = (e) => {
                          const selectedImage = e.target.files[0];
                          uploadFile(selectedImage);
                        };
                        input.click();
                      }}
                    >
                      Replace
                    </Button>
                  </div>
                </div>
              )}

              {!data?.imageUrl && <ImageDropTarget onChange={(file) => uploadFile(file)} />}
            </div>
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[16px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl tw-max-h-[469px]">
          <Typography variant="subtitle-m-bold">About The Market</Typography>

          <div className="tw-flex tw-justify-between tw-items-center tw-gap-[8px]">
            <Typography className="!tw-text-gray-50">Custom Text</Typography>
            <AiAssistant
              isTinyMce
              textareaId={EDITOR_ID}
              messageType="BlogPost"
              onInsertClick={(text) => {
                const html = remark()
                  .use([remarkGfm, remarkRehype, rehypeSanitize, rehypeStringify])
                  .processSync(text || "")
                  .toString();
                // Insert into this panel's editor, not whichever one holds the cursor.
                tinyMCE.get(EDITOR_ID).execCommand("mceInsertContent", false, html);
              }}
            />
          </div>
          <BrivityTextEditor
            id={EDITOR_ID}
            value={data?.body || ""}
            onchange={() => {}}
            // placeholders={emailPlaceholders}
            settings={{
              min_height: 365,
              height: 365,
              plugins: "lists advlist link emoticons",
              setup: (editor) => {
                let initialized = false;
                let userHasEdited = false;
                editor.on("init", () => {
                  initialized = true;
                });
                editor.on("input", () => {
                  userHasEdited = true;
                });
                editor.on("ExecCommand", (e) => {
                  if (e.command === "mceInsertContent") userHasEdited = true;
                });
                const commit = debounce(() => {
                  if (!initialized || !userHasEdited || editor.removed) return;
                  setMarketStatsData({ ...dataRef.current, body: editor.getContent() });
                }, 500);
                commitRef.current = commit;
                editor.on("input NodeChange", commit);
                editor.on("blur", () => {
                  if (initialized && !editor.removed) flushSync(() => commit.flush());
                });
              },
            }}
          />
        </div>
      </div>
    </CollapsibleWebsiteCard>
  );
};

MarketStatsPanel.propTypes = {
  data: PropTypes.shape({
    uuid: PropTypes.string,
    isActive: PropTypes.bool,
    eyebrow: PropTypes.string,
    stat1Value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stat2Value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stat3Value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dateUpdated: PropTypes.string,
    imageUrl: PropTypes.string,
    body: PropTypes.string,
  }),
  setMarketStatsData: PropTypes.func.isRequired,
};

export default MarketStatsPanel;
